import React, { useState } from 'react';
import { UserProfile, Facility } from '../../../app/domain/types/index.ts';
import { MapPin, Search, Filter, Star, Navigation, Phone, Clock, DollarSign, Dumbbell, Utensils, ShoppingCart, Stethoscope, CheckCircle } from 'lucide-react';

interface FacilityLocatorProps {
  user: UserProfile;
}

const FacilityLocator: React.FC<FacilityLocatorProps> = ({ user }) => {
  const [activeCategory, setActiveCategory] = useState<'gym' | 'restaurant' | 'grocery' | 'medical'>('gym');
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // View Mode is primarily for mobile. Desktop shows split view.
  const [mobileViewMode, setMobileViewMode] = useState<'list' | 'map'>('list');

  // Mock Data - Curated "Zero-Noise" Results
  const facilities: Facility[] = [
    {
      id: '1',
      name: 'FitLife Gym',
      category: 'gym',
      subcategory: 'Fitness Center',
      address: '123 Main St, Houston, TX',
      distance: '2.3 mi',
      rating: 4.8,
      reviewCount: 128,
      isAffordable: true,
      isOpen: true,
      tags: ['Beginner Friendly', 'No Contract', 'Yoga Classes'],
      priceRange: '$'
    },
    {
      id: '2',
      name: 'Iron Paradise',
      category: 'gym',
      subcategory: 'Weightlifting',
      address: '456 Oak Ave, Houston, TX',
      distance: '3.1 mi',
      rating: 4.9,
      reviewCount: 85,
      isAffordable: false,
      isOpen: true,
      tags: ['Heavy Weights', '24/7 Access'],
      priceRange: '$$'
    },
    {
      id: '3',
      name: 'Green Kitchen',
      category: 'restaurant',
      subcategory: 'Organic Cafe',
      address: '789 Pine Ln, Houston, TX',
      distance: '1.5 mi',
      rating: 4.7,
      reviewCount: 210,
      isAffordable: false,
      isOpen: true,
      tags: ['Organic', 'Gluten-Free', 'Low-Carb Options'],
      priceRange: '$$'
    },
    {
      id: '4',
      name: 'Budget Bites',
      category: 'restaurant',
      subcategory: 'Healthy Fast Casual',
      address: '101 Maple Dr, Houston, TX',
      distance: '0.8 mi',
      rating: 4.5,
      reviewCount: 340,
      isAffordable: true,
      isOpen: false,
      tags: ['Meal Prep', 'High Protein', 'Under $15'],
      priceRange: '$'
    },
    {
      id: '5',
      name: 'Whole Foods Market',
      category: 'grocery',
      subcategory: 'Supermarket',
      address: '202 Cedar Blvd, Houston, TX',
      distance: '2.0 mi',
      rating: 4.6,
      reviewCount: 500,
      isAffordable: false,
      isOpen: true,
      tags: ['Organic Produce', 'Specialty Diet'],
      priceRange: '$$$'
    },
    {
      id: '6',
      name: 'Community Clinic',
      category: 'medical',
      subcategory: 'General Practice',
      address: '303 Elm St, Houston, TX',
      distance: '4.5 mi',
      rating: 4.4,
      reviewCount: 56,
      isAffordable: true,
      isOpen: true,
      tags: ['Sliding Scale', 'Diabetes Specialist'],
      priceRange: '$'
    }
  ];

  // Filter Logic
  const filteredFacilities = facilities.filter(f => {
    const matchesCategory = f.category === activeCategory;
    const matchesAffordable = showAffordableOnly ? f.isAffordable : true;
    const matchesSearch = f.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          f.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesAffordable && matchesSearch;
  });

  const categories = [
    { id: 'gym', label: 'Gyms', icon: Dumbbell },
    { id: 'restaurant', label: 'Food', icon: Utensils },
    { id: 'grocery', label: 'Groceries', icon: ShoppingCart },
    { id: 'medical', label: 'Medical', icon: Stethoscope },
  ];

  // Component for the List
  const FacilityList = () => (
    <div className="grid grid-cols-1 gap-4 animate-fade-in-up pb-24 md:pb-0">
        {filteredFacilities.length > 0 ? (
            filteredFacilities.map((facility) => (
                <div key={facility.id} className="glass-panel rounded-2xl p-0 overflow-hidden group hover:shadow-lg transition-all duration-300 border border-stone-100 dark:border-stone-700 hover:border-health-200 dark:hover:border-health-800 bg-white dark:bg-stone-800/80">
                    {/* Mock Image Area */}
                    <div className="h-32 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-700 dark:to-stone-800 relative">
                            <div className="absolute top-3 right-3 bg-white/90 dark:bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-sm">
                            {facility.distance}
                            </div>
                            {facility.isAffordable && (
                                <div className="absolute top-3 left-3 bg-trust-500 text-white px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
                                    <DollarSign size={10} /> Affordable
                                </div>
                            )}
                            <div className="absolute bottom-3 left-3 flex gap-1">
                                {facility.tags.slice(0, 2).map(tag => (
                                    <span key={tag} className="px-2 py-0.5 bg-black/60 backdrop-blur-md text-white text-[10px] rounded-full">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                    </div>

                    <div className="p-5">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white group-hover:text-health-700 dark:group-hover:text-health-400 transition-colors">{facility.name}</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{facility.subcategory}</p>
                            </div>
                            <div className="flex items-center gap-1 bg-stone-50 dark:bg-stone-700 px-1.5 py-0.5 rounded-md border border-stone-100 dark:border-stone-600">
                                <Star size={12} className="text-orange-400 fill-orange-400" />
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{facility.rating}</span>
                                <span className="text-[10px] text-gray-400">({facility.reviewCount})</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                            <MapPin size={14} className="text-gray-400" />
                            <span className="truncate">{facility.address}</span>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-stone-100 dark:border-stone-700">
                            <div className="flex flex-col">
                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Status</span>
                                <span className={`text-xs font-medium flex items-center gap-1 ${facility.isOpen ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                    <Clock size={12} /> {facility.isOpen ? 'Open Now' : 'Closed'}
                                </span>
                            </div>
                            
                            <div className="flex gap-2">
                                <button className="p-2 rounded-full bg-stone-50 dark:bg-stone-700 text-gray-600 dark:text-gray-300 hover:bg-health-50 dark:hover:bg-health-900 hover:text-health-600 dark:hover:text-health-400 transition-colors border border-stone-200 dark:border-stone-600">
                                    <Phone size={16} />
                                </button>
                                <button className="p-2 rounded-full bg-stone-50 dark:bg-stone-700 text-gray-600 dark:text-gray-300 hover:bg-trust-50 dark:hover:bg-trust-900 hover:text-trust-600 dark:hover:text-trust-400 transition-colors border border-stone-200 dark:border-stone-600">
                                    <Navigation size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ))
        ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center mb-4">
                    <Filter size={24} />
                </div>
                <p>No facilities found matching your filters.</p>
                <button 
                    onClick={() => {setSearchQuery(''); setShowAffordableOnly(false);}}
                    className="mt-4 text-health-600 dark:text-health-400 font-medium text-sm hover:underline"
                >
                    Clear Filters
                </button>
            </div>
        )}
    </div>
  );

  // Component for the Map
  const MapView = () => (
    <div className="w-full h-full min-h-[500px] glass-panel rounded-3xl overflow-hidden relative flex items-center justify-center bg-stone-200 dark:bg-stone-800 animate-fade-in-up border border-stone-100 dark:border-stone-700">
        <div className="absolute inset-0 opacity-20 dark:opacity-10 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/Neighborhood_Map.png')] bg-cover bg-center grayscale"></div>
        <div className="relative z-10 bg-white/90 dark:bg-stone-900/90 backdrop-blur-md p-6 rounded-2xl shadow-xl max-w-sm text-center border border-white dark:border-stone-700">
            <div className="w-12 h-12 bg-health-100 dark:bg-health-900/30 text-health-600 dark:text-health-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin size={24} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Map Integration</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Interactive Google Maps integration is enabled in Phase 2. Currently showing list view data for {activeCategory}s in {user.location}.
            </p>
        </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col relative">
      
      {/* Sticky Header Area */}
      <div className="p-6 md:p-8 pb-4 bg-[#FAFAFA]/90 dark:bg-[#0f0f0f]/90 backdrop-blur-md z-20 border-b border-stone-200 dark:border-stone-800 sticky top-0">
        <div className="mb-4">
            <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">Facility Locator</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                <MapPin size={14} className="text-health-600 dark:text-health-400" />
                Showing curated results near <span className="font-medium text-gray-900 dark:text-white">{user.location || 'Houston, TX'}</span>
            </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-4">
            {/* Search & Map Toggle (Mobile Only) */}
            <div className="flex gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                        type="text" 
                        placeholder={`Search ${activeCategory}s...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl shadow-sm focus:outline-none focus:border-health-500 focus:ring-1 focus:ring-health-500/20 transition-all text-sm text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                </div>
                {/* Mobile Toggle */}
                <div className="flex md:hidden bg-stone-100 dark:bg-stone-800 p-1 rounded-xl">
                    <button 
                        onClick={() => setMobileViewMode('list')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${mobileViewMode === 'list' ? 'bg-white dark:bg-stone-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                        List
                    </button>
                    <button 
                        onClick={() => setMobileViewMode('map')}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${mobileViewMode === 'map' ? 'bg-white dark:bg-stone-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                        Map
                    </button>
                </div>
            </div>

            {/* Categories & Filters */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex gap-2 overflow-x-auto w-full md:w-auto no-scrollbar pb-1">
                    {categories.map((cat) => {
                        const Icon = cat.icon;
                        const isActive = activeCategory === cat.id;
                        return (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat.id as any)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all whitespace-nowrap ${
                                    isActive 
                                    ? 'bg-health-600 border-health-600 text-white shadow-md shadow-health-600/20' 
                                    : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-gray-600 dark:text-gray-300 hover:border-health-300 dark:hover:border-health-700 hover:bg-health-50 dark:hover:bg-health-900/20'
                                }`}
                            >
                                <Icon size={16} />
                                <span className="text-sm font-medium">{cat.label}</span>
                            </button>
                        )
                    })}
                </div>

                <button
                    onClick={() => setShowAffordableOnly(!showAffordableOnly)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all text-sm font-medium ${
                        showAffordableOnly 
                        ? 'bg-trust-50 dark:bg-trust-900/20 border-trust-200 dark:border-trust-800 text-trust-700 dark:text-trust-400 ring-2 ring-trust-100 dark:ring-trust-900/30' 
                        : 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                    <DollarSign size={16} className={showAffordableOnly ? 'text-trust-600 dark:text-trust-400' : 'text-gray-400'} />
                    Affordable Only
                    {showAffordableOnly && <CheckCircle size={14} className="ml-1 text-trust-600 dark:text-trust-400" />}
                </button>
            </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
          {/* Desktop Split View - Always active on md+ screens */}
          <div className="hidden md:flex h-full">
              <div className="w-1/3 h-full overflow-y-auto no-scrollbar p-6 pt-0 border-r border-stone-100 dark:border-stone-800">
                  <FacilityList />
              </div>
              <div className="w-2/3 h-full p-6 pt-0 relative">
                   <MapView />
              </div>
          </div>

          {/* Mobile Toggle View - Only on small screens */}
          <div className="md:hidden h-full overflow-y-auto p-6 pt-0">
              {mobileViewMode === 'list' ? <FacilityList /> : <MapView />}
          </div>
      </div>

    </div>
  );
};

export default FacilityLocator;